/**
 * The parent Print dialog on Orders (PROD-1064): print a child's week or
 * month of orders, one page per student. Same composition as the school
 * Print dialog (title, pill sections, one Print button); submit opens
 * `/orders/family/print` in a new tab.
 *
 * - Student: every student, or one. Hidden for a one-student household.
 * - Range: Week (Monday to Friday, stepped with the arrows, starting on the
 *   week on screen) or Month (one month from a start date the parent picks,
 *   starting on the first of next month).
 *
 * A native <form onSubmit> (app Forms rule): Print is the sole
 * `type="submit"`; pills, arrows and the date field are `type="button"` /
 * native inputs. Registered in `CONVERTED_FORMS`.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { Skeleton } from '@ordo/ui/skeleton'
import { CalendarX, ChevronLeft, ChevronRight } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router'

import { useFamilyOrdersRangeQuery } from '@/api/family-orders'
import type { OrderingStudent } from '@/api/orders-family'
import {
  countDaysWithOrders,
  familyPrintHref,
  type FamilyPrintRange,
  firstOfNextMonth,
  formatPrintRange,
  monthPrintRange,
  weekPrintRange,
} from '@/lib/family-orders/print'
import { addDaysKey, mondayOf, todayKey } from '@/lib/school-orders/calendar'

/** The school Print dialog's pill toggles (`school-print-dialog.tsx`), so both dialogs read the same. */
const TOGGLE_PILL =
  'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1'
const TOGGLE_PILL_ON = 'border-primary bg-primary/10 text-primary'
const TOGGLE_PILL_OFF = 'border-border bg-background text-foreground hover:bg-muted'

const RANGE_LABEL: Record<FamilyPrintRange, string> = { week: 'Week', month: 'Month' }

export interface FamilyPrintDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The household's students, in the order the Orders page lists them. */
  students: readonly OrderingStudent[]
  /** Any day of the week on screen; Week starts there. */
  weekAnchorKey: string
}

export function FamilyPrintDialog({ open, onOpenChange, students, weekAnchorKey }: FamilyPrintDialogProps) {
  const navigate = useNavigate()
  const monthStartId = useId()
  const [studentId, setStudentId] = useState<string | null>(null)
  const [range, setRange] = useState<FamilyPrintRange>('week')
  const [weekMonday, setWeekMonday] = useState(() => mondayOf(weekAnchorKey))
  const [monthStart, setMonthStart] = useState(() => firstOfNextMonth(todayKey()))

  // Each opening starts on the week the parent is looking at.
  useEffect(() => {
    if (open) {
      setWeekMonday(mondayOf(weekAnchorKey))
    }
  }, [open, weekAnchorKey])

  const { from, to } = range === 'week' ? weekPrintRange(weekMonday) : monthPrintRange(monthStart)
  const ordersQuery = useFamilyOrdersRangeQuery({ from, to, enabled: open })
  const printed = studentId ? students.filter((student) => student.userId === studentId) : students
  const days = ordersQuery.data
    ? countDaysWithOrders(ordersQuery.data.orders, new Set(printed.map((student) => student.userId)), from, to)
    : null
  const pages = printed.length
  // Spelled out: the selection is the thing a parent reads before printing.
  const rangeLabel = formatPrintRange(from, to, { longMonth: true })
  const rangeWithYear = formatPrintRange(from, to, { longMonth: true, withYear: true })

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (!days) {
      return
    }
    const href = familyPrintHref({ range, from, to, studentId })
    const win = window.open(href, '_blank')
    if (!win) {
      // Popup blocked: same-tab navigation is the graceful fallback.
      void navigate(href)
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Print</DialogTitle>
          <DialogDescription>Breakfast and lunch orders, one page per student.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="flex flex-col gap-6">
            {students.length > 1 ? (
              <section className="flex flex-col gap-2.5">
                <p className="text-sm font-medium text-foreground">Student</p>
                <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Student">
                  {[null, ...students.map((student) => student.userId)].map((id) => {
                    const on = studentId === id
                    const label =
                      id === null
                        ? 'All students'
                        : (students.find((student) => student.userId === id)?.displayName ?? '')
                    return (
                      <button
                        key={id ?? 'all'}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setStudentId(id)}
                        className={cn(TOGGLE_PILL, on ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF)}>
                        {label}
                      </button>
                    )
                  })}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2.5">
              <p className="text-sm font-medium text-foreground">Range</p>
              <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Range">
                {(['week', 'month'] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={range === key}
                    onClick={() => setRange(key)}
                    className={cn(TOGGLE_PILL, range === key ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF)}>
                    {RANGE_LABEL[key]}
                  </button>
                ))}
              </div>

              {range === 'week' ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-muted-foreground">Week of</span>
                  <div
                    role="group"
                    aria-label="Week"
                    className="flex h-11 min-w-0 flex-1 items-center justify-between gap-1 rounded-md border border-input bg-background px-1 sm:w-80 sm:flex-none">
                    <button
                      type="button"
                      aria-label="Previous week"
                      onClick={() => setWeekMonday(addDaysKey(weekMonday, -7))}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                      <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
                    </button>
                    <span className="truncate text-base font-semibold tabular-nums text-foreground">{rangeLabel}</span>
                    <button
                      type="button"
                      aria-label="Next week"
                      onClick={() => setWeekMonday(addDaysKey(weekMonday, 7))}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                      <ChevronRight className="h-5 w-5" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <label htmlFor={monthStartId} className="text-sm text-muted-foreground">
                    Start date
                  </label>
                  <Input
                    id={monthStartId}
                    type="date"
                    value={monthStart}
                    onChange={(event) => {
                      if (/^\d{4}-\d{2}-\d{2}$/.test(event.target.value)) {
                        setMonthStart(event.target.value)
                      }
                    }}
                    className="h-11 w-48 text-base"
                  />
                </div>
              )}
            </section>

            {/* What will print: the exact days and the page count, or why there is nothing to print. */}
            {days === null ? (
              <Skeleton className="h-[76px] w-full rounded-lg" />
            ) : days === 0 ? (
              <div role="status" className="flex items-start gap-3 rounded-lg border bg-muted/40 px-4 py-3.5">
                <CalendarX className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
                <div className="flex min-w-0 flex-col gap-1">
                  <p className="text-base font-semibold text-foreground">No orders for {rangeWithYear}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {range === 'week' ? 'Pick another week to print.' : 'Pick another start date to print.'}
                  </p>
                </div>
              </div>
            ) : (
              <div role="status" className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3.5">
                <p className="text-base font-semibold text-foreground">{rangeWithYear}</p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium tabular-nums text-foreground">
                    {days} {days === 1 ? 'day' : 'days'}
                  </span>{' '}
                  with orders ·{' '}
                  <span className="font-medium tabular-nums text-foreground">
                    {pages} {pages === 1 ? 'page' : 'pages'}
                  </span>
                  {pages > 1 ? ', one per student' : null}
                </p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="submit" disabled={!days}>
              Print
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
